export const WV2EVENTS = {
  SelectionChanged: "SelectionChanged",
  PackageExecutionCompleted: "PackageExecutionCompleted",
  RevitInstanceList: "RevitInstanceList",
  ConnectionStatus: "ConnectionStatus",
  RegistryStatus: "RegistryStatus",
  getEventName(key) {
    return this[key]?.toLowerCase();
  },
};

export const DIRECT_INSTANCE_ID = "local-direct";

const eventCaptureElement = document.createElement("a");
const searchParams = new URLSearchParams(window.location.search);
const revitBridgeUrl = searchParams.get("revitBridgeUrl");
const revitBridgeToken = searchParams.get("revitBridgeToken");
const xanoApiBase = searchParams.get("xanoApiBase");
const pendingBridgeMessages = [];
const pendingRegistryMessages = [];
const webClientId = getOrCreateWebClientId();

let bridgeSocket = null;
let bridgeMode = false;
let registrySocket = null;
let registryMode = false;

const protocolActionAliases = {
  "revit-instance-list": WV2EVENTS.RevitInstanceList,
  "connection-status": WV2EVENTS.ConnectionStatus,
  "selection-changed": WV2EVENTS.SelectionChanged,
  "package-execution-completed": WV2EVENTS.PackageExecutionCompleted,
};

function getOrCreateWebClientId() {
  const key = "revit-web-client-id";
  const existing = window.localStorage.getItem(key);
  if (existing) {
    return existing;
  }

  const id = `web-${Math.random().toString(16).slice(2)}-${Date.now()}`;
  window.localStorage.setItem(key, id);
  return id;
}

function dispatchRevitEvent({ action, payload }) {
  const normalizedAction = protocolActionAliases[action] || action;
  const e = WV2EVENTS.getEventName(normalizedAction);
  if (e !== undefined) {
    eventCaptureElement.dispatchEvent(new CustomEvent(e, { detail: payload }));
  }
}

function registryUrl() {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/revit-registry/web`;
}

function startBridge() {
  if (!revitBridgeUrl || !revitBridgeToken || bridgeSocket) {
    return;
  }

  const separator = revitBridgeUrl.includes("?") ? "&" : "?";
  bridgeSocket = new WebSocket(`${revitBridgeUrl}${separator}token=${encodeURIComponent(revitBridgeToken)}`);

  bridgeSocket.addEventListener("open", () => {
    bridgeMode = true;
    dispatchRevitEvent({ action: WV2EVENTS.RegistryStatus, payload: { direct: true } });
    while (pendingBridgeMessages.length > 0 && bridgeSocket.readyState === WebSocket.OPEN) {
      bridgeSocket.send(JSON.stringify(pendingBridgeMessages.shift()));
    }
  });

  bridgeSocket.addEventListener("message", (event) => {
    try {
      const message = JSON.parse(event.data);
      if (message.action === WV2EVENTS.SelectionChanged) {
        message.payload = {
          instanceId: DIRECT_INSTANCE_ID,
          elements: message.payload || [],
        };
      }
      if (message.action === WV2EVENTS.PackageExecutionCompleted) {
        message.payload = {
          instanceId: DIRECT_INSTANCE_ID,
          ...(message.payload || {}),
        };
      }
      dispatchRevitEvent(message);
    } catch (_error) {
      // Ignore malformed bridge messages.
    }
  });

  bridgeSocket.addEventListener("close", () => {
    bridgeMode = false;
  });

  bridgeSocket.addEventListener("error", () => {
    bridgeMode = false;
  });
}

function startRegistry() {
  if (registrySocket) {
    return;
  }

  registrySocket = new WebSocket(registryUrl());

  registrySocket.addEventListener("open", () => {
    registryMode = true;
    sendRegistry({
      action: "register-web",
      payload: {
        webClientId,
        name: `${window.navigator.platform || "Browser"} Web UI`,
        origin: window.location.origin,
      },
    });
    while (pendingRegistryMessages.length > 0 && registrySocket.readyState === WebSocket.OPEN) {
      registrySocket.send(JSON.stringify(pendingRegistryMessages.shift()));
    }
    dispatchRevitEvent({ action: WV2EVENTS.RegistryStatus, payload: { registry: true } });
  });

  registrySocket.addEventListener("message", (event) => {
    try {
      dispatchRevitEvent(JSON.parse(event.data));
    } catch (_error) {
      // Ignore malformed registry messages.
    }
  });

  registrySocket.addEventListener("close", () => {
    registryMode = false;
    registrySocket = null;
    dispatchRevitEvent({ action: WV2EVENTS.RegistryStatus, payload: { registry: false } });
  });

  registrySocket.addEventListener("error", () => {
    registryMode = false;
  });
}

function sendRegistry(message) {
  if (registrySocket?.readyState === WebSocket.OPEN) {
    registrySocket.send(JSON.stringify(message));
    return;
  }

  pendingRegistryMessages.push(message);
  startRegistry();
}

startBridge();
startRegistry();

window.dispatchWebViewEvent = function dispatchWebViewEvent(message) {
  dispatchRevitEvent(message);
};

export function subscribeToWebView2Event(eventName, handler) {
  const e = WV2EVENTS.getEventName(eventName);
  if (e === undefined) {
    return;
  }

  eventCaptureElement.addEventListener(e, handler);
}

export function unsubscribeToWebView2Event(eventName, handler) {
  const e = WV2EVENTS.getEventName(eventName);
  if (e === undefined) {
    return;
  }

  eventCaptureElement.removeEventListener(e, handler);
}

export function connectRegistryInstance(targetInstanceId) {
  sendRegistry({
    action: "connect-request",
    payload: {
      webClientId,
      targetInstanceId,
      origin: window.location.origin,
    },
  });
}

export function disconnectRegistryInstance(targetInstanceId) {
  sendRegistry({
    action: "disconnect",
    payload: {
      webClientId,
      targetInstanceId,
    },
  });
}

export function requestRevitInstanceList() {
  sendRegistry({
    action: "list-instances",
    payload: {
      webClientId,
    },
  });
}

export function postRevitMessage({ action, payload }, targetInstanceIds = []) {
  if (!action) {
    return;
  }

  const targets = targetInstanceIds.length > 0 ? targetInstanceIds : [DIRECT_INSTANCE_ID];
  const directTargets = targets.filter((id) => id === DIRECT_INSTANCE_ID);
  const registryTargets = targets.filter((id) => id !== DIRECT_INSTANCE_ID);

  if (directTargets.length > 0 && revitBridgeUrl && revitBridgeToken) {
    const message = { action, payload };
    if (bridgeSocket?.readyState === WebSocket.OPEN) {
      bridgeSocket.send(JSON.stringify(message));
    } else {
      pendingBridgeMessages.push(message);
      startBridge();
    }
  }

  if (registryTargets.length > 0) {
    sendRegistry({
      action,
      payload: {
        requestId: payload?.id,
        targetInstanceIds: registryTargets,
        packageRequest: payload,
      },
    });
  }
}

export function postWebView2Message(message, targetInstanceIds) {
  postRevitMessage(message, targetInstanceIds);
}

export function getDirectBridgeInstance() {
  if (!revitBridgeUrl || !revitBridgeToken) {
    return null;
  }

  return {
    instanceId: DIRECT_INSTANCE_ID,
    machineName: "Local",
    windowsUser: "",
    revitVersion: "",
    documentTitle: "Revit that opened this browser",
    status: bridgeMode ? "connected" : "pending",
    direct: true,
  };
}

export function getWebClientId() {
  return webClientId;
}

export function getXanoApiBase() {
  return xanoApiBase || null;
}

export function isInWebViewContext() {
  return bridgeMode || registryMode || !!revitBridgeUrl || !!window.chrome?.webview;
}
